import type { ClassRoom, TimetableSlot } from '@/lib/types';
import { formatTime } from '@/lib/constants';

// 항목별로 표시할 학생 이름을 계산해요.
// 항목에 별도 명단이 있으면(students_customized) 그 명단을, 없으면 연결된 반 명단을 사용해요.
export function resolveSlotStudents(
  slots: TimetableSlot[],
  classRoster: Record<string, string[]>,
  slotRoster: Record<string, string[]>
): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  slots.forEach((s) => {
    out[s.id] = s.students_customized
      ? slotRoster[s.id] || []
      : classRoster[s.class_id] || [];
  });
  return out;
}

export const UNASSIGNED_TEACHER_ID = '__unassigned__';

export type TeacherGroup = {
  id: string;
  name: string;
  isUnassigned: boolean;
  slots: TimetableSlot[];
};

// 반의 담당 선생님(teacher_id)을 기준으로 시간표를 선생님별로 묶어요.
// 담당이 없는 반은 '담당 미배정' 그룹으로 모으고 항상 맨 뒤에 둡니다.
export function buildTeacherGroups(
  classes: ClassRoom[],
  slots: TimetableSlot[],
  teacherName: (id: string) => string
): TeacherGroup[] {
  const classTeacher = new Map<string, string>();
  classes.forEach((c) => {
    classTeacher.set(c.id, c.teacher_id || UNASSIGNED_TEACHER_ID);
  });

  const groups = new Map<string, TeacherGroup>();
  slots.forEach((slot) => {
    const key = classTeacher.get(slot.class_id) || UNASSIGNED_TEACHER_ID;
    if (!groups.has(key)) {
      groups.set(key, {
        id: key,
        name: key === UNASSIGNED_TEACHER_ID ? '담당 미배정' : teacherName(key),
        isUnassigned: key === UNASSIGNED_TEACHER_ID,
        slots: [],
      });
    }
    groups.get(key)!.slots.push(slot);
  });

  groups.forEach((g) => {
    g.slots.sort(
      (a, b) =>
        a.day_of_week - b.day_of_week || a.start_time.localeCompare(b.start_time)
    );
  });

  return Array.from(groups.values()).sort((a, b) => {
    if (a.isUnassigned) return 1;
    if (b.isUnassigned) return -1;
    return a.name.localeCompare(b.name, 'ko');
  });
}

export type PrintEntry = {
  className: string;
  subject?: string | null;
  time: string;
  teacher?: string;
  students?: string[];
};

export type PrintDay = {
  day: number;
  entries: PrintEntry[];
};

// 인쇄용으로 요일(월~일) 순서대로 항목을 정리해요.
export function buildPrintDays(
  slots: TimetableSlot[],
  classMap: Map<string, ClassRoom>,
  teacherMap: Map<string, string>,
  studentsBySlot: Record<string, string[]>,
  showTeacher: boolean
): PrintDay[] {
  const order = [1, 2, 3, 4, 5, 6, 0];
  return order.map((day) => ({
    day,
    entries: slots
      .filter((s) => s.day_of_week === day)
      .sort((a, b) => a.start_time.localeCompare(b.start_time))
      .map((s) => {
        const c = classMap.get(s.class_id);
        const teacher = c?.teacher_id ? teacherMap.get(c.teacher_id) : '담당 미배정';
        return {
          className: c?.name || '삭제된 반',
          subject: c?.subject ?? null,
          time: `${formatTime(s.start_time)}~${formatTime(s.end_time)}`,
          teacher: showTeacher ? teacher || '담당 미배정' : undefined,
          students: studentsBySlot[s.id] || [],
        };
      }),
  }));
}